import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { merge } from "lodash";

import SelectProvider from "components/custom-filters-list/filters/select-provider.component";
import DateStringFormField from "components/date-string-form-field";
import {
  DEFAULT_DATE_QUICK_OPTIONS,
  defaultOnQuickOptionClick
} from "components/date-string-form-field/date-string-form-field.component";
import InputFormField from "components/input-form-field";
import ToggleButtonGroupFormField from "components/toggle-button-group-form-field/toggle-button-group-form-field.component";
import WordProcessorField from "components/word-processor-field";
import { TASK_PRIORITY_OPTIONS } from "constants/staff-tasks";
import { TASK_TEMPLATE_TYPES } from "constants/templates";

import PatientFieldWithSearch from "../../../components/patient-field-with-search";
import { useGetPatientQuery } from "../../../services/patients";
import useStaffTasksV2 from "../hooks/use-staff-tasks-v2.hook";
import {
  EMPTY_DEFAULT_TASK_VALUES,
  type StaffTaskV2FormValues
} from "../staff-task.utils";
import StaffTaskAttachments, {
  type StaffTaskAttachmentsRef
} from "./staff-task-modal-dialog/staff-task-attachments";

export default function StaffTaskForm() {
  const attachmentsRef = useRef<StaffTaskAttachmentsRef>(null);

  const {
    handleOpenTaskDrawer,
    handleCreate,
    isUpdating,
    isPatientViewMode: isDrawerPatientViewMode,
    patientId
  } = useStaffTasksV2();

  const { data: patient } = useGetPatientQuery(
    {
      patientId: Number(patientId),
      query: "{id,first_name,middle_name,last_name}"
    },
    {
      skip: !patientId || patientId === -1
    }
  );

  const defaultValues: StaffTaskV2FormValues = useMemo(() => {
    return merge({}, EMPTY_DEFAULT_TASK_VALUES, {
      patient: patient || null
    });
  }, [patient]);

  const formMethods = useForm<StaffTaskV2FormValues>({
    mode: "onBlur",
    defaultValues
  });

  const {
    watch,
    control,
    reset,
    handleSubmit,
    formState: { isValid }
  } = formMethods;

  const assignee = useWatch({ control, name: "assignee" });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = useCallback(
    (data: StaffTaskV2FormValues) => {
      handleCreate(data, async (createdTaskId) => {
        if (createdTaskId && attachmentsRef.current?.hasPendingFiles()) {
          await attachmentsRef.current.uploadPendingFiles(createdTaskId);
        }
      });
    },
    [handleCreate]
  );

  return (
    <Box sx={{ pt: 2 }} component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          mb: 1,
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: "24px"
          }}
        >
          New Task
        </Typography>
      </Stack>
      <Grid
        item
        container
        xs={12}
        spacing={2}
        sx={{ mb: 3, width: "100%", pl: 3, pr: 1 }}
      >
        <Grid item xs={12}>
          <ToggleButtonGroupFormField
            name="priority"
            label="Priority"
            options={TASK_PRIORITY_OPTIONS}
            control={control}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <InputFormField
            name="title"
            label="Title"
            control={control}
            required
            autoFocus
            sx={{ ml: 0 }}
          />
        </Grid>
        <Grid item xs={12}>
          <SelectProvider
            isActive={true}
            name="assignee"
            label="Assignee"
            includeOtherClinics
            ignoreRegionsFilter
            ignoreClinicsFilter
            control={control}
            required
            sx={{ ml: 0 }}
          />
        </Grid>
        <Grid item xs={12}>
          <SelectProvider
            isActive
            name="assignees"
            label="Additional Assignees"
            includeOtherClinics
            ignoreRegionsFilter
            ignoreClinicsFilter
            control={control}
            multiple
            enableSelectAll
            excludeSelectAllValues={assignee?.value}
            sx={{ ml: 0 }}
          />
        </Grid>
        <Grid item xs={12}>
          <PatientFieldWithSearch
            label="Patient"
            control={control}
            required={isDrawerPatientViewMode}
          />
        </Grid>
        <Grid item xs={12}>
          <DateStringFormField
            name="due_at"
            label="Due Date"
            control={control}
            PopperProps={{
              style: { zIndex: 14001 }
            }}
            quickOptions={DEFAULT_DATE_QUICK_OPTIONS}
            onQuickOptionClick={defaultOnQuickOptionClick}
            sx={{ ml: 0, pr: 0, minWidth: "100%", "& > div": { flexGrow: 1 } }}
          />
        </Grid>

        <Grid item xs={12}>
          <WordProcessorField
            name="description"
            control={control}
            placeholder="Enter task details"
            height={280}
            featuresGroup="textOnly"
            allowTemplates
            allowMergeTags
            allowInsertMergeTagsValues
            templateTypes={TASK_TEMPLATE_TYPES}
            allowToolbarAutohide={false}
            allowUndoRedo={false}
            allowHtml={false}
            allowPasteAsText={false}
            mergeTagsPatientId={Number(watch("patient")?.id) || -1}
          />
        </Grid>
        <Grid item xs={12}>
          <StaffTaskAttachments ref={attachmentsRef} taskId={undefined} />
        </Grid>
      </Grid>
      <Stack
        sx={{ pr: 3 }}
        justifyContent="flex-end"
        spacing={2}
        direction="row"
      >
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            handleOpenTaskDrawer("view");
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          disabled={!isValid}
          loading={isUpdating}
          variant="contained"
          size="small"
          type="submit"
        >
          Save
        </LoadingButton>
      </Stack>
    </Box>
  );
}
